import React from 'react'
import { Container, Typography, Box, Grid, useTheme, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import ModernFooter from '../Components/ModernFooter';

import {
  Work,
  BusinessCenter,
  School,
  TrendingUp,
} from '@mui/icons-material';

import {
  fadeInUp,
  staggerChildren,
  staggerItem,
} from '../styles/animations';

function Career() {
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
            {/* Hero Section - Design Amélioré */}
            <Box 
                sx={{
                    background: 'linear-gradient(135deg, #003d7a 0%, #1e5ba8 30%, #ff6b35 100%)',
                    color: 'white',
                    py: { xs: 6, md: 10 },
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Éléments décoratifs animés */}
                <Box
                    component={motion.div}
                    animate={{
                        scale: [1, 1.1, 1],
                        rotate: [0, 5, 0],
                    }}
                    transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                    sx={{
                        position: 'absolute',
                        top: '10%',
                        right: '5%',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.1)',
                        filter: 'blur(20px)',
                    }}
                />
                
                <Container maxWidth="lg">
                    <motion.div
                        variants={fadeInUp}
                        initial="initial"
                        whileInView="animate"
                        viewport={{ once: true, amount: 0.3 }}
                    >
                        {/* Badge moderne */}
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Box
                                component={motion.div}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.2 }}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    backdropFilter: 'blur(10px)',
                                    padding: '12px 24px',
                                    borderRadius: '50px',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    fontWeight: 600,
                                    fontSize: '0.9rem',
                                }}
                            >
                                <TrendingUp sx={{ fontSize: 20 }} />
                                {t("contenu.texte44")}
                            </Box>
                        </Box>
                        
                        <Typography
                            variant="h2"
                            component="h1"
                            align="center"
                            gutterBottom
                            sx={{ 
                                fontWeight: 800, 
                                mb: 3,
                                fontSize: { xs: '2.5rem', md: '3.5rem' },
                                lineHeight: 1.1,
                                textShadow: '2px 2px 8px rgba(0,0,0,0.3)',
                            }}
                        >
                            {t("contenu.texte42")}
                        </Typography>
                        <Typography
                            variant="h5"
                            align="center"
                            sx={{ 
                                opacity: 0.95, 
                                maxWidth: 700, 
                                mx: 'auto',
                                fontSize: { xs: '1.1rem', md: '1.3rem' },
                                lineHeight: 1.6,
                                mb: 4,
                            }}
                        >
                            {t("contenu.texte43")}
                        </Typography>
                        
                        {/* Boutons CTA améliorés */}
                        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                size="large"
                                endIcon={<Work />}
                                sx={{
                                    background: 'white',
                                    color: '#003d7a',
                                    fontWeight: 700,
                                    px: 4,
                                    py: 2,
                                    borderRadius: '50px',
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                                    '&:hover': {
                                        background: 'rgba(255, 255, 255, 0.9)',
                                        transform: 'translateY(-3px)',
                                        boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
                                    },
                                }}
                            >
                                {t("contenu.texte45")}
                            </Button>
                            
                            <Button
                                variant="outlined"
                                size="large"
                                startIcon={<School />}
                                sx={{
                                    borderColor: 'white',
                                    color: 'white',
                                    fontWeight: 600,
                                    px: 4,
                                    py: 2,
                                    borderRadius: '50px',
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    backdropFilter: 'blur(10px)',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        borderColor: 'white',
                                        transform: 'translateY(-3px)',
                                    },
                                }}
                            >
                                {t("contenu.texte46")}
                            </Button>
                        </Box>
                    </motion.div>
                </Container>
            </Box>

            {/* Section Pourquoi NKH - Design Moderne */}
            <Box sx={{ 
                py: { xs: 6, md: 10 }, 
                background: 'linear-gradient(180deg, rgba(0, 61, 122, 0.05) 0%, rgba(255, 107, 53, 0.05) 100%)',
                position: 'relative'
            }}>
                <Container maxWidth="lg">
                    <motion.div
                        variants={fadeInUp}
                        initial="initial"
                        whileInView="animate"
                        viewport={{ once: true, amount: 0.3 }}
                    >
                        <Typography
                            variant="h3"
                            component="h2"
                            align="center"
                            gutterBottom
                            sx={{ 
                                fontWeight: 700, 
                                mb: 4,
                                color: '#003d7a',
                                fontSize: { xs: '2rem', md: '2.5rem' }
                            }}
                        >
                            {t("contenu.texte47")}
                        </Typography>
                        
                        <Grid container spacing={4} sx={{ mt: 4 }}>
                            {[
                                { icon: <Work />, title: t("contenu.texte48"), desc: t("contenu.texte49") },
                                { icon: <BusinessCenter />, title: t("contenu.texte50"), desc: t("contenu.texte51") },
                                { icon: <School />, title: t("contenu.texte52"), desc: t("contenu.texte53") },
                                { icon: <TrendingUp />, title: t("contenu.texte54"), desc: t("contenu.texte55") },
                            ].map((item, index) => (
                                <Grid item xs={12} md={6} lg={3} key={index}>
                                    <motion.div
                                        variants={staggerItem}
                                        initial="initial"
                                        whileInView="animate"
                                        viewport={{ once: true, amount: 0.3 }}
                                    >
                                        <Box sx={{
                                            textAlign: 'center',
                                            p: 3,
                                            height: '100%',
                                            background: 'white',
                                            borderRadius: 3,
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-5px)',
                                                boxShadow: '0 8px 30px rgba(0,61,122,0.15)',
                                            }
                                        }}>
                                            <Box sx={{ 
                                                color: '#ff6b35', 
                                                fontSize: 40, 
                                                mb: 2,
                                                display: 'flex',
                                                justifyContent: 'center'
                                            }}>
                                                {item.icon}
                                            </Box>
                                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#003d7a' }}>
                                                {item.title}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                                {item.desc}
                                            </Typography>
                                        </Box>
                                    </motion.div>
                                </Grid>
                            ))}
                        </Grid>
                    </motion.div>
                </Container>
            </Box>

            {/* Section Opportunités - Design Amélioré */}
            <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: theme.palette.background.paper }}>
                <Container maxWidth="lg">
                    <motion.div
                        variants={fadeInUp}
                        initial="initial"
                        whileInView="animate"
                        viewport={{ once: true, amount: 0.3 }}
                    >
                        <Box sx={{ textAlign: 'center', mb: 6 }}>
                            <Typography
                                variant="h3"
                                component="h2"
                                gutterBottom
                                sx={{ 
                                    fontWeight: 700, 
                                    mb: 2,
                                    color: '#003d7a',
                                    fontSize: { xs: '2rem', md: '2.5rem' }
                                }}
                            >
                                {t("contenu.texte56")}
                            </Typography>
                            {/* <Typography
                                variant="h6"
                                sx={{ 
                                    color: 'text.secondary',
                                    maxWidth: 600,
                                    mx: 'auto',
                                    lineHeight: 1.6
                                }}
                            >
                                {t("contenu.texte57")}
                            </Typography> */}
                        </Box>
                        
                        <motion.div
                            variants={staggerChildren}
                            initial="initial"
                            whileInView="animate"
                            viewport={{ once: true, amount: 0.3 }}
                        >
                            {/* <Grid container spacing={4}>
                                {Formations.map((formation, index) => (
                                    <Grid item xs={12} sm={6} md={3} key={index}>
                                        <motion.div variants={staggerItem}>
                                            <ModernCard
                                                variant="service"
                                                image=""
                                                title={formation.title.split("|")[i18n.language.toLowerCase() === "fr" ? 0 : 1]}
                                                description={formation.desc?.split("|")[i18n.language.toLowerCase() === "fr" ? 0 : 1] || "Poste disponible"}
                                                icon={careerIcons[index]}
                                                link="/contact"
                                                linkText="Postuler"
                                            />
                                        </motion.div>
                                    </Grid>
                                ))}
                            </Grid> */}
                        </motion.div>
                    </motion.div>
                </Container>
            </Box>
            
            <ModernFooter />
        </Box>
    )
}

export default Career